import { World } from '@/commons/world/World';
import { WorldDebugPanel } from '@/commons/world/WorldDebugPanel';
import { addEnvironmentFolder } from '@/commons/environment/EnvironmentTweakPane';
import { EnvironmentController } from '@/commons/environment/EnvironmentController';
import { renderPreset } from '@/commons/renderPresetToggleList/RenderPreset';
import { applyRenderPreset } from '@/commons/renderPresetToggleList/applyRenderPreset';
import { ArcRotateCamera } from '@/commons/camera/ArcRotateCamera';
import { Events } from '@/services/Events';
import { createGlassDebugPanel } from '@/littleWedding/physicalMaterialSetup';
import { CameraDebugPanel } from '@/commons/camera/CameraDebugPanel';
import { ModelLoader } from '@/commons/assetsManager/ModelLoader';
import { TweakPanel } from '@/commons/TweakPanel';
import { initGobeletMaterial } from '@/littleWedding/gobeletMaterialSetup';
import { LightHelper } from '@/commons/lights/LightHelper';
import {
    Mesh,
    Object3D,
    PCFShadowMap,
    MeshStandardMaterial,
    PlaneGeometry,
    MeshBasicMaterial,
    TextureLoader,
    DoubleSide,
    type Object3DEventMap,
    Group,
    BufferGeometry,
} from 'three';
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js';
import type { TCameraParams } from '@/commons/camera/TCameraParams';
import type { TWorldParams } from '@/commons/world/TWorldParams';
import type { CustomOrbitControls } from '@/commons/camera/controls/CustomOrbitControls';
import type { EGobeletType } from '@/littleWedding/EGobeletType';

class Wedding3DWorld extends World {
    private static _instance: Wedding3DWorld;

    initialRotationY: number;
    _camera: ArcRotateCamera;
    customMaterial: MeshBasicMaterial | MeshStandardMaterial;
    isZoomed = false;
    _environmenController: EnvironmentController;
    productType: string | EGobeletType;
    private isMobile: boolean; // Add mobile detection as class property
    private gobeletBoundingBox: { depth: number; height: number; width: number } | null = null; // Store gobelet dimensions
    private contextRestoreRetries = 0; // Track context restore attempts
    private maxRetries = 3; // Maximum number of retry attempts
    private readonly RETRY_DELAY = 1000; // Delay between retries in milliseconds

    get camera(): ArcRotateCamera {
        return this._camera;
    }

    static get scene() {
        return Wedding3DWorld.instance?.scene;
    }

    static get renderer() {
        return Wedding3DWorld.instance?.renderer;
    }

    static get environmentMap() {
        return this.instance?._environmenController.getEnvironmentMap;
    }

    static animateCamera(isAnimated: boolean) {
        if (!Wedding3DWorld.instance) return;
        (Wedding3DWorld.instance.camera.controls as CustomOrbitControls).autoRotate = isAnimated;
    }

    static setZoomLvl(zoomValue: number, duration = 750): Promise<void> | undefined {
        if (!Wedding3DWorld.instance) return;
        switch (zoomValue) {
            case 0:
                // setZoom far
                // eslint-disable-next-line consistent-return
                return Wedding3DWorld.instance.camera.zoomOnSelection(
                    [Wedding3DWorld.instance.scene.getObjectByName('cup') || new Object3D()],
                    1.4,
                    duration,
                );
            case 1:
                // setZoom medium
                // eslint-disable-next-line consistent-return
                return Wedding3DWorld.instance.camera.zoomOnSelection(
                    [Wedding3DWorld.instance.scene.getObjectByName('cup') || new Object3D()],
                    1.2,
                    duration,
                );
            case 2:
                // zoom near
                // eslint-disable-next-line consistent-return
                return Wedding3DWorld.instance.camera.zoomOnSelection(
                    [Wedding3DWorld.instance.scene.getObjectByName('cup') || new Object3D()],
                    1,
                    duration,
                );
            default:
                // setZoom far
                // eslint-disable-next-line consistent-return
                return Wedding3DWorld.instance.camera.zoomOnSelection(
                    [Wedding3DWorld.instance.scene.getObjectByName('cup') || new Object3D()],
                    1.3,
                    duration,
                );
        }
    }

    constructor(params: TWorldParams) {
        super(params);
        Wedding3DWorld._instance = this;
        // TODO we should use directly EGobeletType but glb model are named internally using 33/50 from phase 1
        // When backend will provide directly the 3D model we can switch to EGobeletType and modify glb internal naming
        const matchResult = params.productType.match(/(\d+)/);
        const produtTypeAsInt = matchResult && matchResult[0] ? matchResult[0].toString() : '';
        this.productType = `${produtTypeAsInt}`;

        // Mobile detection - store as class property
        this.isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;

        // Initialize environment controller first
        this._environmenController = new EnvironmentController();

        // Configure renderer with mobile optimizations
        this._renderer.shadowMap.enabled = !this.isMobile; // Disable shadows completely on mobile
        this._renderer.pixelRatio = this.isMobile
            ? Math.min(window.devicePixelRatio, 1.2) // Further reduced for mobile
            : Math.min(window.devicePixelRatio, 2);

        // Improve depth precision to reduce z-fighting
        this._renderer.sortObjects = true;

        if (this._renderer.shadowMap.enabled) {
            this._renderer.shadowMap.type = PCFShadowMap;
            this._renderer.shadowMap.autoUpdate = true;
            this._renderer.shadowMap.needsUpdate = true;
        }

        const cameraParams: TCameraParams = {
            aspect: window.innerWidth / window.innerHeight,
            near: this.isMobile ? 0.5 : 0.2, // Increase near plane for better depth precision
            far: this.isMobile ? 5 : 6, // Reduce far plane for better depth precision
            fov: 40,
            name: 'main-camera',
            rendererDomElement: this.renderer.domElement,
        };
        this._mainLoop.addCallback(() => {
            this._camera.controls.update();
            this._camera.render();
        });
        LightHelper.init();
        RectAreaLightUniformsLib.init();

        this._camera = new ArcRotateCamera(cameraParams);

        // Safe environment initialization
        try {
            this._environmenController.initialize(this.scene, this.renderer);
        } catch (error) {
            console.error('Failed to initialize environment controller:', error);
        }

        this.customMaterial = params.customMaterial || new MeshStandardMaterial();
        this.configureCamera();
        this.importModel();

        // Add context loss/restore handlers for mobile
        const canvas = this.renderer.domElement;
        canvas.addEventListener('webglcontextlost', this.handleContextLoss.bind(this));
        canvas.addEventListener('webglcontextrestored', this.handleContextRestore.bind(this));

        // Safe debug panel initialization (desktop only)
        if (!this.isMobile && TweakPanel.isInitialized) {
            try {
                WorldDebugPanel.init();
                CameraDebugPanel.initMenu();

                if (this._environmenController) {
                    addEnvironmentFolder(this._environmenController);
                    applyRenderPreset(this._environmenController, renderPreset.default);
                }
            } catch (error) {
                console.warn('Failed to initialize debug panels:', error);
            }
        }

        this._mainLoop.start();
        this.resize();

        window.addEventListener('wheel', this.wheelEvent);
    }

    createGround() {
        // Use shadow image for both mobile and desktop for consistency
        // Check if shadow already exists to avoid duplicates
        const existingShadow = this.scene.getObjectByName('ground-shadow');
        if (!existingShadow) {
            // Creating new shadow
            this.createShadowImage();
        } else {
            // Shadow already exists, updating size based on gobelet dimensions
            this.updateShadowSize();
        }
    }

    createShadowImage() {
        // Use a texture-based shadow that adapts to gobelet size
        const shadowSize = this.computeShadowSize();
        const shadowGeometry = new PlaneGeometry(shadowSize.width, shadowSize.depth);

        // Load the shadow texture from the textures folder
        const textureLoader = new TextureLoader();

        // Create material first with a temporary texture (more visible for debugging)
        const shadowMaterial = new MeshBasicMaterial({
            transparent: true,
            opacity: 0.45,
            side: DoubleSide,
            depthWrite: false, // Prevent depth writing to avoid z-fighting
            depthTest: true, // Keep depth testing for proper occlusion
        });

        const shadowPlane = new Mesh(shadowGeometry, shadowMaterial);
        shadowPlane.name = 'ground-shadow';
        shadowPlane.rotation.x = -Math.PI / 2;
        shadowPlane.position.y = -0.002; // Slightly lower to ensure it's below the glass
        shadowPlane.renderOrder = -1; // Render before other objects to avoid z-fighting

        this.scene.add(shadowPlane);

        // Load the shadow texture asynchronously
        textureLoader.load(
            '/models/textures/gobelet-shadow.webp',
            // onLoad callback
            (texture) => {
                shadowMaterial.map = texture;
                shadowMaterial.color.setHex(0xffffff); // Reset to white to show texture properly
                shadowMaterial.needsUpdate = true;
            },
            // onProgress callback
            undefined,
            // onError callback
            () => {
                this.createFallbackTexture(shadowMaterial);
            },
        );
    }

    createFallbackTexture(material: MeshBasicMaterial) {
        // Create programmatic shadow texture as fallback
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const context = canvas.getContext('2d')!;

        // Create radial gradient for shadow
        const gradient = context.createRadialGradient(128, 128, 0, 128, 128, 80);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.4)');
        gradient.addColorStop(0.4, 'rgba(0, 0, 0, 0.15)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

        context.fillStyle = gradient;
        context.fillRect(0, 0, 256, 256);

        const fallbackTexture = new TextureLoader().load(canvas.toDataURL());
        material.map = fallbackTexture;
        material.color.setHex(0xffffff);
        material.needsUpdate = true;
    }

    computeShadowSize() {
        if (!this.gobeletBoundingBox) {
            return { width: 0.8, depth: 0.8 };
        }
        const baseSize = Math.min(this.gobeletBoundingBox.width, this.gobeletBoundingBox.depth);
        const shadowScale = 0.95; // 1.04 - original size reduced by 20%
        const shadowSize = baseSize * shadowScale;

        if (shadowSize < 0.1 || shadowSize > 20) {
            console.warn(`Shadow size seems unreasonable: ${shadowSize.toFixed(3)}, using fallback`);
            return { width: 2.0, depth: 2.0 }; // Reasonable fallback size
        }

        return {
            width: shadowSize,
            depth: shadowSize, // Keep it square/circular for a natural shadow
        };
    }

    updateShadowSize() {
        // Update existing shadow size based on gobelet dimensions
        const existingShadow = this.scene.getObjectByName('ground-shadow') as Mesh;
        if (existingShadow && this.gobeletBoundingBox) {
            const newSize = this.computeShadowSize();
            const currentGeometry = existingShadow.geometry as PlaneGeometry;

            // Instead of scaling (which can distort), recreate the geometry with the correct size
            // This ensures the shadow remains circular/square and properly sized
            currentGeometry.dispose(); // Clean up old geometry
            existingShadow.geometry = new PlaneGeometry(newSize.width, newSize.depth);
        } else if (!existingShadow) {
            console.warn('No shadow found to update');
        }
    }

    configureCamera() {
        this.camera.position.x = -0.75;
        this.camera.position.z = 2.75;
        this.camera.setPhi(1.1815);
        this.camera.setTheta(3.11375);
        this.camera.setRadius(2.59);
        this._camera.controls.enableDamping = true;
        this._camera.controls.dampingFactor = this.isMobile ? 0.25 : 0.15; // More damping on mobile for smoother controls
        this._camera.controls.enablePan = false;
        this._camera.controls.rotateSpeed = this.isMobile ? 1.0 : 1.5; // Slower rotation on mobile
        this._camera.near = 0.2; // Better depth precision for double-walled geometry
        this._camera.far = 6; // Tighter far plane for better precision
        this.camera.controls.maxDistance = 5;
        this.camera.controls.minDistance = 1.8;
        (this._camera.controls as CustomOrbitControls).autoRotateSpeed = -4.5;
        const { maxPolarAngle } = this._camera.controls as CustomOrbitControls;
        (this._camera.controls as CustomOrbitControls).minPolarAngle = maxPolarAngle * 0.37;
        (this._camera.controls as CustomOrbitControls).maxPolarAngle = maxPolarAngle * 0.56;
    }

    importModel() {
        const modelName = 'cup';

        // Add error handling for model loading
        const modelPromise = ModelLoader.loadModel(`/models/SM_Gobelet_${this.productType}.glb`);

        if (!modelPromise?.promise) {
            console.error('Failed to initiate model loading');
            return;
        }

        modelPromise.promise
            .then((model) => {
                if (!model?.scene) {
                    throw new Error('Model scene is undefined');
                }

                model.scene.scale.set(100, 100, 100);
                const savedChild: Mesh[] = this.setupModelOnLoad(model.scene);

                model.scene.name = modelName;
                this.scene.add(model.scene);

                // Create ground with proper shadow sizing after model is loaded and dimensions are calculated
                // Model loading complete, creating/updating shadow with proper dimensions
                this.createGround();
                this.setupCameraOnModelLoaded(savedChild);
            })
            .catch((error) => {
                console.error('Failed to load 3D model:', error);
                // Could emit an event here to notify the UI of the failure
                Events.emit('model-load-error', error.message);
            });
    }

    /**
     * Computes the bounding box of a mesh geometry.
     * @param meshGeometry Useful to compute bounding box of a geometry relative to a scale factor if needed.
     * @param scaleFactor The scale factor to apply to the bounding box dimensions.
     * @returns The computed bounding box dimensions.
     */
    computeBoundingBoxSize(meshGeometry: BufferGeometry, scaleFactor: number = 10): { depth: number; height: number; width: number } {
        const meshCopy = meshGeometry.clone();
        meshCopy.computeBoundingBox();
        const { boundingBox } = meshCopy;
        if (!boundingBox) {
            console.warn('Bounding box is undefined, returning default dimensions');
            return { width: 1, height: 1, depth: 1 };
        }
        // Calculate geometry dimensions for shadow sizing
        return {
            width: (boundingBox.max.x - boundingBox.min.x) * scaleFactor,
            height: (boundingBox.max.y - boundingBox.min.y) * scaleFactor,
            depth: (boundingBox.max.z - boundingBox.min.z) * scaleFactor,
        };
    }

    getBboxCenterCoordinate(meshGeometry: BufferGeometry, scaleFactor: number = 10) {
        const meshCopy = meshGeometry.clone();
        meshCopy.computeBoundingBox();
        if (!meshCopy.boundingBox) {
            console.warn('Bounding box is undefined, returning default coordinates');
            return { x: 0, y: 0, z: 0 };
        }
        const bBoxYPos = (meshCopy.boundingBox.max.y - meshCopy.boundingBox.min.y) * scaleFactor * 0.5;
        const bBoxXPos = (meshCopy.boundingBox.max.x - meshCopy.boundingBox.min.x) * scaleFactor * 0.5;
        const bBoxZPos = (meshCopy.boundingBox.max.z - meshCopy.boundingBox.min.z) * scaleFactor * 0.5;

        return { x: bBoxXPos, y: bBoxYPos, z: bBoxZPos };
    }

    setupModelOnLoad(glbScene: Group<Object3DEventMap>) {
        glbScene.scale.set(100, 100, 100);
        const savedChild: Mesh[] = [];

        glbScene.traverse((child) => {
            if (child instanceof Mesh) {
                try {
                    const scalefactor = 0.1;
                    child.scale.set(scalefactor, scalefactor, scalefactor);
                    this.gobeletBoundingBox = this.computeBoundingBoxSize(child.geometry);
                    const { y } = this.getBboxCenterCoordinate(child.geometry, scalefactor);
                    this.camera.controls.target.y = y;
                    this.camera.position.y = y;

                    // Mobile optimization: completely disable shadow casting on mobile
                    child.castShadow = false;
                    child.receiveShadow = false;

                    if (child.name === `SM_Gobelet_${this.productType}_1`) {
                        // Fix geometry normals to eliminate stripes
                        if (child.geometry) {
                            child.geometry.computeVertexNormals();
                            child.geometry.normalizeNormals();
                        }
                        child.material = initGobeletMaterial();
                        child.castShadow = false;
                        child.receiveShadow = false;
                        child.name = 'cup_inside';

                        if (!this.isMobile && TweakPanel.isInitialized) {
                            try {
                                createGlassDebugPanel(
                                    TweakPanel.getFolderOrTabByName()?.addFolder({ title: child.name, expanded: false }),
                                    child.material,
                                );
                            } catch (error) {
                                console.warn('Failed to create debug panel:', error);
                            }
                        }
                    }

                    if (child.name === `SM_Gobelet_${this.productType}_2`) {
                        child.name = 'cup_customizable';
                        child.material = this.customMaterial;
                        child.castShadow = false;
                        child.receiveShadow = false;
                        child.renderOrder = 1; // Render before the glass
                    }

                    child.visible = false;
                    savedChild.push(child);
                } catch (error) {
                    console.error('Error processing mesh child:', error);
                }
            }
        });
        return savedChild;
    }

    setupCameraOnModelLoaded(models: Mesh[]) {
        // Safe zoom with error handling
        const zoomPromise = Wedding3DWorld.setZoomLvl(0, 0);
        if (zoomPromise) {
            zoomPromise
                .then(() => {
                    this.camera.setPhi(1.1815);
                    this.camera.setTheta(3.11375);
                    models.forEach((child) => {
                        child.visible = true;
                    });
                })
                .catch((error) => {
                    console.error('Error during zoom initialization:', error);
                    // Fallback: make children visible anyway
                    models.forEach((child) => {
                        child.visible = true;
                    });
                });
        } else {
            // Fallback if zoom fails
            models.forEach((child) => {
                child.visible = true;
            });
        }
    }

    static zoomOnConfigPart(offset = 0) {
        const configurePart = Wedding3DWorld.instance?.scene.getObjectByName('Configurable Part');
        if (!configurePart) return;
        Wedding3DWorld.instance?.camera.zoomOnSelection([configurePart], 0.45 + offset);
    }

    handleContextLoss(event: Event) {
        event.preventDefault();
        console.warn('WebGL context lost. Attempting to recover...');
        this._mainLoop.stop();
    }

    handleContextRestore() {
        console.warn('WebGL context restored, restarting main loop...');
        try {
            this._environmenController.initialize(this.scene, this.renderer);
            this._mainLoop.start();
            console.warn('WebGL context successfully restored');
        } catch (error) {
            this.contextRestoreRetries++;
            if (this.contextRestoreRetries < this.maxRetries) {
                console.warn(`Retrying context restoration (${this.contextRestoreRetries}/${this.maxRetries})...`);
                setTimeout(() => this.handleContextRestore(), this.RETRY_DELAY);
            } else {
                console.error('Failed to restore WebGL context:', error);
            }
        }
    }

    dispose(): void {
        this._camera.dispose();
        window.removeEventListener('wheel', this.wheelEvent);

        // Remove context event listeners
        const canvas = this.renderer.domElement;
        canvas.removeEventListener('webglcontextlost', this.handleContextLoss.bind(this));
        canvas.removeEventListener('webglcontextrestored', this.handleContextRestore.bind(this));

        super.dispose();
    }

    wheelEvent = () => {
        const limitRadius = 0.6;
        if (!this.isZoomed && this._camera.radius < limitRadius) {
            this.isZoomed = true;
            Events.emit('zoomed', !this.isZoomed);
        }
        if (this.isZoomed && this._camera.radius > limitRadius) {
            this.isZoomed = false;
            Events.emit('zoomed', !this.isZoomed);
        }
    };

    public static get instance(): Wedding3DWorld | undefined {
        if (!Wedding3DWorld._instance) return undefined;

        return Wedding3DWorld._instance;
    }
}

export { Wedding3DWorld };
